import { Grid, ListItem, ListItemButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  borderRadius,
  height,
  lighten,
  minHeight,
  minWidth,
  width
} from '@mui/system'
import Avatar from 'components/badges/Avatar'
import { he } from 'date-fns/locale'
import { APP_COLOR } from 'utils/colors'

export const Card = styled(Grid)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(4, 5),
  backgroundColor: theme.palette.card.primary,
  borderRadius: 20,
  boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.3)',
  '&[data-padding="false"]': {
    padding: 0
  }
}))

export const PrivateLabel = styled('div')(({ theme }) => ({
  color: 'white',
  height: '16px',
  alignContent: 'center',
  padding: '0 0.25rem',
  backgroundColor: '#423636',
  borderRadius: '7px',
  lineHeight: '16px',
  fontSize: '12px'
}))

export const GroupListItem = styled(ListItem)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 0, 0, 0),

  cursor: 'pointer',
  backgroundColor: theme.palette.card.primary
}))

export const GroupCardItem = styled(Card)(({ theme }) => ({
  height: 'auto',
  minHeight: '50px',
  width: '100%',
  display: 'flex',
  boxShadow: '0px 4px 10px rgba(177, 186, 200, 0.2)',
  borderRadius: '20px',
  margin: theme.spacing(2, 0),
  padding: 0
}))

export const GroupCardButton = styled(ListItemButton)(({ theme }) => ({}))

interface GroupCardAvatarProps {
  size?: number | undefined
}

export const GroupCardAvatar = styled(Avatar)<GroupCardAvatarProps>(
  (props) => ({
    width: props.size || 42,
    height: props.size || 42,
    minWidth: props.size || 42,
    minHeight: props.size || 42
  })
)

interface GroupCardAvatarContainerProps {
  color: string
  borderWidth?: string
  width?: number
  height?: number
}

export const GroupCardAvatarContainer = styled('div')(
  (props: GroupCardAvatarContainerProps) => ({
    width: props.width ?? 50,
    height: props.height ?? 50,

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `${props.borderWidth ?? '2px'} solid ${props.color}`,
    borderRadius: 9999,
    '&[data-size="sm"]': {
      width: 26,
      height: 26,
      minWidth: 26,
      minHeight: 26
    },
    '&[data-size="md"]': {
      width: 46,
      height: 46,
      minWidth: 46,
      minHeight: 46,
      '@media (max-height: 500px)': {
        width: 30,
        height: 30,
        minWidth: 30,
        minHeight: 30
      }
    },
    '@media (max-height: 500px)': {
      width: 26,
      height: 26,
      minWidth: 26,
      minHeight: 26
    }
  })
)

export const GroupCardContent = styled(Grid)(({ theme }) => ({
  margin: theme.spacing(0, 2),
  width: 'calc(100% - 50px)',
  minHeight: '71px',

  paddingTop: 6,
  paddingBottom: 6,
  borderBottom: '1px solid',
  borderColor: theme.palette.border.primary
}))

export const GroupCardTitleContainer = styled(Grid)(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}))

export const GroupCardTitle = styled('p')(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  maxWidth: 'calc(100% - 75px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}))

export const GroupCardTime = styled('p')(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap'
}))

export const GroupCardInfoContainer = styled(Grid)(() => ({
  display: 'flex',
  minHeight: '34px',
  justifyContent: 'space-between',
  alignItems: 'center'
}))

export const GroupInfoWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}))

export const GroupCardInfo = styled('div')(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  minHeight: 34,
  display: 'box',
  overflow: 'hidden',
  lineClamp: 1,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}))

export const GroupIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  justifyContent: 'flex-end',

  '& svg': {
    color: theme.palette.text.secondary,
    width: 16,
    '&[data-icon="hide"]': {
      width: 20
    }
  }
}))

export const UnreadAmount = styled('div')<{ count?: number }>(({
  theme,
  count
}) => {
  const getHorizontalPadding = (count: number) => {
    if (count < 10) return '0px' // Single digit - no padding

    return '4px' // Four or more digits - 4px padding
  }

  return {
    minWidth: 18,
    height: 18,
    fontSize: 12,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    color: 'white',
    borderRadius: 9, // Half of height for more rounded appearance
    background: '#007fff',
    padding: `0 ${count ? getHorizontalPadding(count) : '0px'}`,
    whiteSpace: 'nowrap',
    lineHeight: 1,
    letterSpacing: '-0.01em'
  }
})
